import { Button } from 'ui/Button';
import { Switch } from '@mantine/core';
import styles from './TelegramSettings.module.scss';
import { ReactComponent as IconBell } from './img/iconBell.svg';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import { CopyToClipboardButton } from 'ui/CopyToClipboardButton';
import ContentLoader from 'react-content-loader';
import { useState, useEffect } from 'react';
import {
  useGenerateTelegramTokenMutation,
  useGetTelegramTokenQuery,
  useResetTelegramTokenMutation,
  useGetTelegramSettingsQuery,
  useDisconnectTelegramAccountMutation,
  useSetTelegramNotificationsMutation,
} from 'redux/Api/profileApi/ProfileApi';
import { TelegramNotificationModal } from './TelegramNotificationModal';

export const TelegramSettings = () => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [isTokenActive, setIsTokenActive] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [expectedDataState, setExpectedDataState] = useState<
    'no-token' | 'has-token' | 'connected' | null
  >(null);

  const {
    data: telegramSettings,
    isLoading: isSettingsLoading,
    refetch: refetchSettings,
  } = useGetTelegramSettingsQuery();

  const {
    data: tokenData,
    isLoading: isTokenLoading,
    refetch: refetchToken,
  } = useGetTelegramTokenQuery(undefined, {
    skip: isSettingsLoading || Boolean(telegramSettings?.username),
  });

  const [generateToken, { isLoading: isGenerating }] = useGenerateTelegramTokenMutation();

  const [resetToken, { isLoading: isResetting }] = useResetTelegramTokenMutation();

  const [disconnectAccount, { isLoading: isDisconnecting }] =
    useDisconnectTelegramAccountMutation();

  const [updateNotifications] = useSetTelegramNotificationsMutation();

  useEffect(() => {
    if (
      tokenData?.code &&
      timeLeft === 0 &&
      !isTokenActive &&
      !telegramSettings?.username
    ) {
      setTimeLeft(tokenData.ttl || 600);
      setIsTokenActive(true);
    }
  }, [tokenData?.code, telegramSettings?.username]);

  useEffect(() => {
    if (!tokenData?.code && (isTokenActive || timeLeft > 0)) {
      setTimeLeft(0);
      setIsTokenActive(false);
    }
  }, [tokenData?.code, isTokenActive, timeLeft]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsTokenActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleEnable = async () => {
    try {
      setExpectedDataState('has-token');
      const result = await generateToken().unwrap();
      setTimeLeft(result.ttl || 600);
      setIsTokenActive(true);
      await refetchToken();
    } catch (error) {
      console.error('Ошибка при генерации токена:', error);
      setExpectedDataState(null);
    }
  };

  const handleCancel = async () => {
    try {
      setExpectedDataState('no-token');
      await resetToken().unwrap();
      await disconnectAccount().unwrap();
      setTimeLeft(0);
      setIsTokenActive(false);
      await refetchToken();
      await refetchSettings();
    } catch (error) {
      console.error('Ошибка при сбросе токена:', error);
      setExpectedDataState(null);
    }
  };

  const handleCreateNew = async () => {
    try {
      setExpectedDataState('has-token');
      await resetToken().unwrap();

      const result = await generateToken().unwrap();
      setTimeLeft(result.ttl || 600);
      setIsTokenActive(true);
      await refetchToken();
    } catch (error) {
      console.error('Ошибка при создании нового токена:', error);
      setExpectedDataState(null);
    }
  };

  const handleDisconnect = async () => {
    try {
      setExpectedDataState('no-token');
      await disconnectAccount().unwrap();
      await resetToken().unwrap();
      setTimeLeft(0);
      setIsTokenActive(false);
      await refetchToken();
      await refetchSettings();
    } catch (error) {
      console.error('Ошибка при отключении аккаунта Telegram:', error);
      setExpectedDataState(null);
    }
  };

  const handleNotificationsToggle = async (enabled: boolean) => {
    try {
      await updateNotifications({
        notifications_enabled: enabled,
      }).unwrap();
      await refetchSettings();
    } catch (error) {
      console.error('Ошибка при обновлении настроек уведомлений:', error);
    }
  };

  const hasToken = Boolean(tokenData?.code);
  const isConnected = Boolean(telegramSettings?.username);
  const hasActiveToken = hasToken && isTokenActive && timeLeft > 0 && !isConnected;
  const hasExpiredToken = hasToken && timeLeft === 0 && !isConnected;
  const showInitialState =
    !isConnected && !hasActiveToken && !hasExpiredToken && !isTokenLoading;

  useEffect(() => {
    if (expectedDataState === 'no-token' && !hasToken) {
      setExpectedDataState(null);
    } else if (expectedDataState === 'has-token' && hasToken && !isConnected) {
      setExpectedDataState(null);
    } else if (expectedDataState === 'connected' && isConnected) {
      setExpectedDataState(null);
    }
  }, [expectedDataState, hasToken, isConnected]);

  const isInitialDataLoading = isTokenLoading || isSettingsLoading;

  const isMutationLoading =
    isGenerating || isResetting || isDisconnecting || expectedDataState !== null;

  return (
    <div className={styles.root}>
      <div className={styles.root_title}>
        <div className={styles.root_title_left}>
          <IconBell />
          <span>Уведомления Telegram</span>
        </div>
        {isConnected && (
          <Switch
            styles={{
              track: {
                width: '44px',
                height: '24px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: telegramSettings?.notifications_enabled
                  ? '#002CFB'
                  : 'rgba(173, 191, 223, 0.40)',
              },
              thumb: {
                border: 'none',
              },
            }}
            checked={telegramSettings?.notifications_enabled ?? true}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handleNotificationsToggle(event.currentTarget.checked);
            }}
          />
        )}
      </div>

      {isInitialDataLoading ? (
        <div className={styles.skeleton_container}>
          <ContentLoader
            speed={2}
            width='100%'
            height={54}
            backgroundColor={'#FFFFFF'}
            foregroundColor={'#D8E4FB8C'}
            style={{ borderRadius: '8px' }}>
            <rect x='0' y='12' rx='4' ry='4' width='320' height='22' />
            <rect x='0' y='42' rx='4' ry='4' width='80' height='32' />
          </ContentLoader>
        </div>
      ) : !isMutationLoading ? (
        <>
          {showInitialState && (
            <div className={styles.root_content}>
              <span className={styles.root_content_text}>
                Получай уведомления о новых сообщениях из чатов с поддержкой.
              </span>
              <Button
                className={styles.btn}
                onClick={handleEnable}
                disabled={isGenerating}
                theme='filed'>
                Включить
              </Button>
            </div>
          )}

          {isConnected && (
            <>
              <div className={styles.root_content} style={{ marginTop: '12px' }}>
                <span className={styles.root_content_text_account}>
                  Аккаунт{' '}
                  <a
                    href={`https://t.me/${telegramSettings?.username}`}
                    target='_blank'
                    rel='noreferrer'
                    className={styles.root_content_text_link}
                    style={{ marginLeft: '4px' }}>
                    @{telegramSettings?.username}
                  </a>
                  <IconClose onClick={handleDisconnect} style={{ cursor: 'pointer' }} />
                </span>
              </div>
              {/* <div
                className={styles.settings}
                onClick={() => setIsNotificationModalOpen(true)}>
                Настройки
              </div> */}
            </>
          )}

          {hasActiveToken && (
            <>
              <div className={styles.root_content} style={{ marginTop: '12px' }}>
                <span className={styles.root_content_text}>
                  Чтобы привязать аккаунт Telegram, отправь код боту{' '}
                  <a
                    href='https://t.me/SkillaSupportBot'
                    target='_blank'
                    rel='noreferrer'
                    className={styles.root_content_text_link}>
                    @SkillaSupportBot
                  </a>{' '}
                  в приложении Telegram
                </span>
              </div>
              <div className={styles.root_content_code}>
                <div className={styles.root_content_code_left}>
                  <span className={styles.root_content_code_text}>
                    Код: {tokenData?.code}{' '}
                    <CopyToClipboardButton
                      content={tokenData?.code || ''}
                      width={24}
                      height={24}
                      showNotification={true}
                    />
                  </span>
                  <span className={styles.root_content_code_text_time}>
                    Срок действия кода истекает через {formatTime(timeLeft)}
                  </span>
                </div>
                <Button
                  className={styles.btn_cancel}
                  theme='outline'
                  onClick={handleCancel}
                  disabled={isResetting}>
                  Отменить
                </Button>
              </div>
            </>
          )}

          {hasExpiredToken && (
            <>
              <div className={styles.root_content} style={{ marginTop: '12px' }}>
                <span className={styles.root_content_text}>
                  Чтобы привязать аккаунт Telegram, отправь код боту{' '}
                  <a
                    href='https://t.me/SkillaSupportBot'
                    target='_blank'
                    rel='noreferrer'
                    className={styles.root_content_text_link}>
                    @SkillaSupportBot
                  </a>{' '}
                  в приложении Telegram
                </span>
              </div>
              <div className={styles.root_content_code}>
                <div className={styles.root_content_code_left}>
                  <span className={styles.root_content_code_text}>
                    Код: {tokenData?.code}{' '}
                    <CopyToClipboardButton
                      content={tokenData?.code || ''}
                      width={24}
                      height={24}
                      showNotification={true}
                    />
                  </span>
                  <span className={styles.root_content_code_text_time}>
                    Срок действия кода истек{' '}
                    <span
                      className={styles.root_content_code_text_time_link}
                      onClick={handleCreateNew}>
                      Создать новый
                    </span>
                  </span>
                </div>
                <Button
                  className={styles.btn_cancel}
                  theme='outline'
                  onClick={handleCancel}
                  disabled={isResetting}>
                  Отменить
                </Button>
              </div>
            </>
          )}
        </>
      ) : null}

      {isConnected && isNotificationModalOpen && (
        <TelegramNotificationModal
          isOpen={isNotificationModalOpen}
          setIsOpen={setIsNotificationModalOpen}
        />
      )}
    </div>
  );
};
